import earcut from 'earcut';
import type { Feature, Geometry, Position } from 'geojson';
import type { CustomLayerInterface, CustomRenderMethodInput, Map as GlobeMap } from 'maplibre-gl';
import { geodesicPoints } from './distance-math.js';
import { globePath } from './globe-geometry.js';
import { globeProjectorFromFrame } from './globe-camera.js';

function ringEnclosesPole(ring: Position[]): boolean {
  let winding = 0;
  for (let index = 0; index < ring.length; index++) {
    const delta = ring[(index + 1) % ring.length][0] - ring[index][0];
    winding += ((delta + 180) % 360 + 360) % 360 - 180;
  }
  return Math.abs(winding) > 180;
}

export function geometryHasPolarCoordinates(geometry: Geometry): boolean {
  switch (geometry.type) {
    case 'Point': return Math.abs(geometry.coordinates[1]) > 85.05112878;
    case 'LineString': case 'MultiPoint': return geometry.coordinates.some(p => Math.abs(p[1]) > 85.05112878);
    case 'Polygon': return geometry.coordinates.some(ring => ringEnclosesPole(ring) || ring.some(p => Math.abs(p[1]) > 85.05112878));
    case 'MultiLineString': return geometry.coordinates.some(line => line.some(p => Math.abs(p[1]) > 85.05112878));
    case 'MultiPolygon': return geometry.coordinates.some(polygon => polygon.some(ring => ringEnclosesPole(ring) || ring.some(p => Math.abs(p[1]) > 85.05112878)));
    case 'GeometryCollection': return geometry.geometries.some(geometryHasPolarCoordinates);
  }
}

export function polarPolygonTriangles(rings: Position[][]): Position[][] {
  const north = rings[0].reduce((sum, p) => sum + p[1], 0) >= 0;
  const points = rings.flat();
  const vertices = points.flatMap(p => { const angle = p[0] * Math.PI / 180; const radius = 90 - (north ? p[1] : -p[1]); return [Math.sin(angle) * radius, Math.cos(angle) * radius]; });
  let offset = 0;
  const holes = rings.slice(0, -1).map(ring => { offset += ring.length; return offset; });
  const indices = earcut(vertices, holes);
  const result: Position[][] = [];
  function subdivide(triangle: Position[], depth: number): void {
    const distances = triangle.map((a, i) => { const b = triangle[(i + 1) % 3]; const delta = ((b[0] - a[0] + 180) % 360 + 360) % 360 - 180; return Math.hypot(delta * Math.cos((a[1] + b[1]) * Math.PI / 360), b[1] - a[1]); });
    const longest = Math.max(...distances);
    if (longest < 3 || depth >= 9) { result.push(triangle); return; }
    const i = distances.indexOf(longest), a = triangle[i], b = triangle[(i + 1) % 3], c = triangle[(i + 2) % 3];
    const midpoint = geodesicPoints(a[1], a[0], b[1], b[0], 2)[1];
    const mid = [midpoint.lng, midpoint.lat];
    subdivide([a, mid, c], depth + 1); subdivide([mid, b, c], depth + 1);
  }
  for (let i = 0; i < indices.length; i += 3) subdivide([points[indices[i]], points[indices[i + 1]], points[indices[i + 2]]], 0);
  return result;
}


type PolarImage = { canvas: HTMLCanvasElement; ratio: number };
type ScreenProjector = (point: Position) => { x: number; y: number };
function paintPolarPoints(context: CanvasRenderingContext2D, points: Position[], props: NonNullable<Feature['properties']>, screen: ScreenProjector, images: ReadonlyMap<string, PolarImage>): void {
      for (const point of points) {
        const p = screen(point); if (!Number.isFinite(p.x + p.y)) continue;
        const image = images.get(props.image);
        if (image) {
          const width = image.canvas.width / image.ratio, height = image.canvas.height / image.ratio;
          const x = p.x + (props.offset?.[0] ?? 0) - width / 2;
          const y = p.y + (props.offset?.[1] ?? 0) - (props.anchor === 'top' ? 0 : props.anchor === 'bottom' ? height : height / 2);
          context.drawImage(image.canvas, x, y, width, height);
        } else { context.beginPath(); context.arc(p.x, p.y, props.radius ?? 6, 0, Math.PI * 2); context.fillStyle = props.color ?? '#3b82f6'; context.fill(); context.strokeStyle = '#ffffff'; context.lineWidth = 2; context.stroke(); }
      }
}


function paintPolarLines(context: CanvasRenderingContext2D, lines: Position[][], screen: ScreenProjector, visible: (point: Position) => boolean, horizon: (inside: Position, outside: Position) => { x: number; y: number }): void {
      for (const line of lines) {
        const dense = globePath(line.map(p => ({ lng: p[0], lat: p[1] })), false);
        context.beginPath();
        for (let i = 1; i < dense.length; i++) {
          const a = dense[i - 1], b = dense[i], va = visible(a), vb = visible(b);
          if (!va && !vb) continue;
          const from = va ? screen(a) : horizon(b, a); const to = vb ? screen(b) : horizon(a, b);
          context.moveTo(from.x, from.y); context.lineTo(to.x, to.y);
        }
        context.stroke();
      }
}

interface PolarDrawing { feature: Feature; triangles: Position[][]; lines: Position[][]; points: Position[] }
export class GlobePolarLayer implements CustomLayerInterface {
  readonly type = 'custom';
  readonly renderingMode = '2d';
  private readonly drawings = new Map<string, PolarDrawing>();
  private readonly images = new Map<string, { canvas: HTMLCanvasElement; ratio: number }>();
  private canvas: HTMLCanvasElement | null = null;
  private program: WebGLProgram | null = null;
  private texture: WebGLTexture | null = null;
  private visible = true;
  constructor(readonly id: string, private readonly map: GlobeMap) {}
  setVisible(visible: boolean): void { this.visible = visible; this.map.triggerRepaint(); }
  image(id: string, canvas: HTMLCanvasElement, ratio: number): void { this.images.set(id, { canvas, ratio }); }
  removeImage(id: string): void { this.images.delete(id); }
  patch(remove: string[], features: Feature[]): void {
    remove.forEach(id => this.drawings.delete(id));
    for (const feature of features) {
      const drawing: PolarDrawing = { feature, triangles: [], lines: [], points: [] };
      const visit = (geometry: Geometry) => {
        switch (geometry.type) {
          case 'Point': drawing.points.push(geometry.coordinates); break;
          case 'MultiPoint': drawing.points.push(...geometry.coordinates); break;
          case 'LineString': drawing.lines.push(geometry.coordinates); break;
          case 'MultiLineString': drawing.lines.push(...geometry.coordinates); break;
          case 'Polygon': drawing.triangles.push(...polarPolygonTriangles(geometry.coordinates)); break;
          case 'MultiPolygon': for (const polygon of geometry.coordinates) drawing.triangles.push(...polarPolygonTriangles(polygon)); break;
          case 'GeometryCollection': geometry.geometries.forEach(visit); break;
        }
      };
      visit(feature.geometry); this.drawings.set(String(feature.id), drawing);
    }
    this.map.triggerRepaint();
  }
  clear(): void { this.drawings.clear(); }
  onAdd(_map: GlobeMap, gl: WebGL2RenderingContext): void {
    const compile = (type: number, source: string) => {
      const shader = gl.createShader(type)!; gl.shaderSource(shader, source); gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) ?? 'Polar shader compilation failed');
      return shader;
    };
    const vertex = compile(gl.VERTEX_SHADER, '#version 300 es\n out vec2 uv; void main(){ vec2 p=vec2(float((gl_VertexID<<1)&2),float(gl_VertexID&2)); uv=vec2(p.x,1.0-p.y); gl_Position=vec4(p*2.0-1.0,0.0,1.0); }');
    const fragment = compile(gl.FRAGMENT_SHADER, '#version 300 es\n precision mediump float; uniform sampler2D image; in vec2 uv; out vec4 color; void main(){color=texture(image,uv);}');
    this.program = gl.createProgram()!; gl.attachShader(this.program, vertex); gl.attachShader(this.program, fragment); gl.linkProgram(this.program);
    gl.deleteShader(vertex); gl.deleteShader(fragment);
    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(this.program) ?? 'Polar shader link failed');
    this.texture = gl.createTexture();
  }
  render(gl: WebGL2RenderingContext, input: CustomRenderMethodInput): void {
    if (!this.visible || !this.drawings.size || !this.program || !this.texture) return;
    this.canvas ??= document.createElement('canvas');
    const canvas = this.canvas;
    canvas.width = this.map.getCanvas().width; canvas.height = this.map.getCanvas().height;
    const context = canvas.getContext('2d')!;
    const ratio = this.map.getPixelRatio();
    context.scale(ratio, ratio);
    const project = globeProjectorFromFrame(input.defaultProjectionData, this.map.getCenter().lng, canvas.width / ratio, canvas.height / ratio);
    const screen = (p: Position) => project({ lng: p[0], lat: p[1] });
    const visible = (point: Position) => { const p = screen(point); return Number.isFinite(p.x + p.y); };
    const horizon = (inside: Position, outside: Position) => {
      let a = inside, b = outside;
      for (let i = 0; i < 24; i++) {
        const middle = geodesicPoints(a[1], a[0], b[1], b[0], 2)[1];
        const next = [middle.lng, middle.lat];
        if (visible(next)) a = next; else b = next;
      }
      return screen(a);
    };
    for (const { feature, triangles, lines, points } of this.drawings.values()) {
      const props = feature.properties ?? {};
      context.globalAlpha = props.fillOpacity ?? 0.2; context.fillStyle = props.fill ?? '#3b82f6';
      context.beginPath();
      for (const triangle of triangles) {
        const projected = triangle.flatMap((a, index) => { const b = triangle[(index + 1) % 3]; const va = visible(a), vb = visible(b); return va ? vb ? [screen(a)] : [screen(a), horizon(a, b)] : vb ? [horizon(b, a)] : []; });
        if (projected.length < 3) continue;
        context.moveTo(projected[0].x, projected[0].y); for (const p of projected.slice(1)) context.lineTo(p.x, p.y); context.closePath();
      }
      context.fill();
      context.globalAlpha = props.opacity ?? 1; context.strokeStyle = props.color ?? '#3b82f6'; context.lineWidth = props.width ?? 3; context.lineCap = 'round'; context.lineJoin = 'round'; context.setLineDash(props.dashed ? [context.lineWidth * 4, context.lineWidth * 3] : []);
      paintPolarLines(context, lines, screen, visible, horizon);
      paintPolarPoints(context, points, props, screen, this.images);
    }
    gl.useProgram(this.program); gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.uniform1i(gl.getUniformLocation(this.program, 'image'), 0);
    gl.disable(gl.DEPTH_TEST); gl.disable(gl.STENCIL_TEST); gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  }
  onRemove(_map: GlobeMap, gl: WebGL2RenderingContext): void { gl.deleteProgram(this.program); gl.deleteTexture(this.texture); this.canvas = null; this.drawings.clear(); this.images.clear(); }
}
